/**
 * ChatBubble Component
 *
 * Floating button that opens the chat window.
 * Shows unread message count badge when applicable.
 * Phase 7: Frontend Integration
 *
 * @module ChatBubble
 */

'use client';

import { IconButton, Box, Badge } from '@axon/ui';
import { useTranslations } from 'next-intl';
import { LuMessageCircle } from 'react-icons/lu';

import { useFeatureFlag } from '@/hooks/useFeatureFlag';

import { useChatbotContext } from '../providers/ChatbotProvider';

// ============================================================================
// Component
// ============================================================================

/**
 * ChatBubble component
 *
 * Floating action button that toggles the chat window.
 * Hidden when AI_CHATBOT feature flag is disabled.
 *
 * @example
 * <ChatBubble />
 */
export function ChatBubble() {
  const isEnabled = useFeatureFlag('AI_CHATBOT');
  const { toggleChat, unreadCount } = useChatbotContext();
  const t = useTranslations('chatbot');

  if (!isEnabled) return null;

  return (
    <Box position="relative">
      <IconButton
        aria-label={t('openChat')}
        data-testid="chat-bubble"
        icon={<LuMessageCircle size={24} />}
        onClick={toggleChat}
        size="lg"
      />

      {unreadCount > 0 && (
        <Box position="absolute" top="-1" right="-1">
          <Badge colorPalette="red" size="sm">
            {unreadCount > 9 ? '9+' : unreadCount}
          </Badge>
        </Box>
      )}
    </Box>
  );
}

export default ChatBubble;
